/**
 * One error model for both transports. `frappe.ts` keeps its own copies until Task 12 deletes it;
 * this is the version everything new imports.
 */

/** Frappe's `exc_type` → our stable codes. */
export const EXC_CODES: Record<string, string> = {
  PermissionError: 'FORBIDDEN',
  ValidationError: 'VALIDATION',
  MandatoryError: 'VALIDATION',
  DoesNotExistError: 'NOT_FOUND',
  DuplicateEntryError: 'DUPLICATE',
  TypeError: 'BAD_REQUEST',
}

/**
 * Frappe writes `_server_messages` for its own Desk UI, which renders them as HTML — so a `>`
 * arrives as `&gt;` and a line break as `<br>`. We render them as TEXT in a toast, where those
 * show through raw: the live `mail.send_email` failure reads "…from Tools &gt; Email Account",
 * which is exactly the sentence telling someone how to fix it.
 *
 * Decoded ONCE, deliberately: `&amp;lt;` must become `&lt;`, not `<`, or a server message could
 * forge markup for anything that later trusts this string.
 */
const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
}

function decodeEntities(text: string): string {
  // `<br>` first — it is markup, not an entity, and Frappe uses it for multi-row validation
  // errors, where losing the break runs every row together into one unreadable line.
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    // One pass over the whole string, so a decoded `&` cannot be re-read as the start of another
    // entity. An entity not in the table is left exactly as it arrived rather than guessed at.
    .replace(/&(?:amp|lt|gt|quot|apos|nbsp|#39);/g, (m) => ENTITIES[m] ?? m)
}

/**
 * `_server_messages` is doubly encoded: a JSON string holding an array whose elements are either
 * JSON strings or plain objects. Both occur in the wild. Never throws.
 */
export function parseServerMessages(raw: unknown): string[] {
  if (typeof raw !== 'string') return []
  let outer: unknown
  try {
    outer = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(outer)) return []
  const out: string[] = []
  for (const entry of outer) {
    let obj: unknown = entry
    if (typeof entry === 'string') {
      try {
        obj = JSON.parse(entry)
      } catch {
        out.push(decodeEntities(entry))
        continue
      }
    }
    if (obj && typeof obj === 'object' && 'message' in obj) {
      const m = (obj as { message: unknown }).message
      if (typeof m === 'string') out.push(decodeEntities(m))
    }
  }
  return out
}

export class BosunApiError extends Error {
  readonly status: number
  readonly code: string
  readonly excType?: string
  readonly messages: string[]

  constructor(status: number, excType: string | undefined, messages: string[], fallback: string) {
    super(messages[0] ?? fallback)
    this.name = 'BosunApiError'
    this.status = status
    this.excType = excType
    this.messages = messages
    this.code = (excType && EXC_CODES[excType]) ?? 'UNKNOWN'
  }
}
